import type { AuthClient, AuthStartResult, AuthVerificationResult } from "./auth-client";
import type { AuthAttempt, AuthCallback, AuthReturnTarget } from "./auth-state";

interface SessionResponse {
  readonly active?: unknown;
  readonly subject?: unknown;
}

interface LogoutResponse {
  readonly endSessionUrl?: unknown;
}

export class BrowserIdentityClient implements AuthClient {
  constructor(
    // Not `= fetch`: native fetch requires its receiver to be `window` (or another Window/
    // WorkerGlobalScope). Storing the bare function reference as `this.fetcher` and later calling
    // it as `this.fetcher(...)` invokes it with `this` bound to the class instance instead, which
    // throws `TypeError: Failed to execute 'fetch' on 'Window': Illegal invocation` — silently
    // caught by restore()'s try/catch, so every real session looked merely "unavailable" instead of
    // never having been requested at all. Wrapping in an arrow function calls the global `fetch` as
    // a free function, which keeps its implicit correct receiver.
    private readonly fetcher: typeof fetch = (...args) => fetch(...args),
    private readonly navigate: (url: string) => void = (url) => window.location.assign(url)
  ) {}

  async start(returnTo: AuthReturnTarget): Promise<AuthStartResult> {
    const target = `/workspace?tool=${returnTo.tool}`;
    this.navigate(`/auth/login?return_to=${encodeURIComponent(target)}`);
    return new Promise<AuthStartResult>(() => undefined);
  }

  async verify(_attempt: AuthAttempt, _callback: AuthCallback): Promise<AuthVerificationResult> {
    return { state: "unavailable", reason: "Live callback verification runs in the Workbench BFF." };
  }

  async restore(): Promise<AuthVerificationResult> {
    try {
      // Explicit even though same-origin fetch already sends cookies by default: the host-only
      // `__Host-workbench_session` cookie must reach this request for a live session to restore.
      const response = await this.fetcher("/auth/session", {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      if (!response.ok) return { state: "unavailable", reason: "Identity session is temporarily unavailable." };
      const value = await response.json() as SessionResponse;
      if (value.active !== true || typeof value.subject !== "string" || value.subject.length === 0) {
        return { state: "refused", reason: "No current Identity session is available." };
      }
      return {
        state: "verified",
        identity: { subject: value.subject, displayName: "Signed-in account", email: "" },
        organizations: []
      };
    } catch {
      return { state: "unavailable", reason: "Identity session is temporarily unavailable." };
    }
  }

  async signOut(): Promise<void> {
    const response = await this.fetcher("/auth/logout", { method: "POST", credentials: "same-origin" });
    if (!response.ok) return;
    // The BFF cannot redirect the top-level page itself (see `functions/auth/logout.ts`'s doc
    // comment: it is called via `fetch()`, which follows redirects internally without ever
    // navigating the visible tab) — it hands back the Identity `/end-session` URL instead, and this
    // is the top-level navigation that actually ends the underlying Identity session so a
    // silent-SSO callback can't sign the browser straight back in.
    const value = await response.json() as LogoutResponse;
    if (typeof value.endSessionUrl === "string") this.navigate(value.endSessionUrl);
  }
}
